import React from 'react';
import { Badge, EyeIcon, PencilIcon, TrashIcon } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { StoreItem } from './useData';

type Props = {
  items: StoreItem[];
  loading?: boolean;
  emptyMessage: string;
  canEdit: boolean;
  canDelete: boolean;
  onView: (row: StoreItem) => void;
  onEdit: (row: StoreItem) => void;
  onDelete: (row: StoreItem) => void;
};

export const StoreItemsTable: React.FC<Props> = ({
  items,
  loading,
  emptyMessage,
  canEdit,
  canDelete,
  onView,
  onEdit,
  onDelete,
}) => {
  const { colors } = useTheme();

  if (loading) {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-2 py-16 text-sm', colors.textMuted)}>
        <div className={cn('h-5 w-5 animate-spin rounded-full border-2 border-t-transparent', colors.border)} />
        Loading items…
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div
        className={cn(
          'm-2 flex flex-col items-center justify-center rounded-xl border border-dashed px-6 py-16 text-center',
          colors.border,
          colors.surfaceHover,
        )}
      >
        <p className={cn('text-sm font-medium', colors.text)}>No store items</p>
        <p className={cn('mt-1 max-w-sm text-xs leading-relaxed', colors.textMuted)}>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto dashboard-scrollbar">
      <table className={cn('w-full table-fixed text-sm', colors.border)}>
        <thead className={cn('sticky top-0 z-10 border-b backdrop-blur-sm', colors.border, colors.surface)}>
          <tr>
            <th className={cn('px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>
              Item
            </th>
            <th
              className={cn(
                'hidden w-28 px-2 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider md:table-cell',
                colors.textMuted,
              )}
            >
              Vendor
            </th>
            <th
              className={cn(
                'hidden w-20 px-2 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider lg:table-cell',
                colors.textMuted,
              )}
            >
              Unit
            </th>
            <th
              className={cn(
                'hidden w-24 px-2 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider md:table-cell',
                colors.textMuted,
              )}
            >
              Stock
            </th>
            <th className={cn('w-20 px-2 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>
              Active
            </th>
            <th className="w-[6.5rem] px-2 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => {
            const meta = [row.vendorCode, row.unit?.ucode].filter(Boolean).join(' · ');
            return (
              <tr
                key={row.id}
                className={cn(
                  'group border-b transition-colors',
                  colors.border,
                  !row.isActive && 'opacity-55',
                  colors.interactiveHover,
                )}
              >
                <td className="px-3 py-2.5 align-middle">
                  <button
                    type="button"
                    onClick={() => onView(row)}
                    className="flex w-full min-w-0 items-start text-left"
                  >
                    <span className="min-w-0 flex-1">
                      <span className={cn('block truncate font-medium group-hover:underline', colors.text)} title={row.name}>
                        {row.name}
                      </span>
                      {meta ? (
                        <span className={cn('mt-0.5 block truncate text-xs tabular-nums md:hidden', colors.textMuted)} title={meta}>
                          {meta}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </td>
                <td className={cn('hidden px-2 py-2.5 align-middle tabular-nums md:table-cell', colors.textSecondary)}>
                  <span className="block truncate" title={row.vendorCode ?? undefined}>
                    {row.vendorCode || '—'}
                  </span>
                </td>
                <td className={cn('hidden px-2 py-2.5 align-middle lg:table-cell', colors.textSecondary)}>
                  {row.unit?.ucode || '—'}
                </td>
                <td className={cn('hidden px-2 py-2.5 align-middle tabular-nums md:table-cell', colors.textSecondary)}>
                  {row.stockMin != null || row.stockMax != null
                    ? `${row.stockMin ?? '—'}–${row.stockMax ?? '—'}`
                    : '—'}
                </td>
                <td className="px-2 py-2.5 align-middle">
                  <Badge
                    variant={row.isActive ? 'success' : 'secondary'}
                    className="rounded px-1.5 py-0 text-[10px] font-medium"
                  >
                    {row.isActive ? 'Active' : 'Off'}
                  </Badge>
                </td>
                <td className="px-2 py-2.5 align-middle">
                  <div
                    className={cn(
                      'flex items-center justify-end gap-0.5 opacity-100 sm:opacity-0 sm:transition-opacity',
                      'sm:group-hover:opacity-100 sm:group-focus-within:opacity-100',
                    )}
                  >
                    <button
                      type="button"
                      className={cn(
                        'rounded-md p-1.5 transition-colors',
                        colors.textMuted,
                        colors.interactiveHover,
                      )}
                      title="View"
                      aria-label="View"
                      onClick={() => onView(row)}
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    {canEdit && (
                      <button
                        type="button"
                        className={cn(
                          'rounded-md p-1.5 transition-colors',
                          colors.primaryFg,
                          colors.interactiveHover,
                        )}
                        title="Edit"
                        aria-label="Edit"
                        onClick={() => onEdit(row)}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        className="rounded-md p-1.5 text-red-500 transition-colors hover:bg-red-500/15"
                        title="Delete"
                        aria-label="Delete"
                        onClick={() => onDelete(row)}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
